/**
 * Transaction List Component
 * 
 * Displays list of transactions with filtering, sorting, and pagination
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Transaction } from '../../types/transaction.types';
import { TransactionItem } from './TransactionItem';
import { BulkCategorisePanel } from './BulkCategorisePanel';
import { BulkNotesPanel } from './BulkNotesPanel';
import { BulkMerchantPanel } from './BulkMerchantPanel';
import { Search, Filter, X, ChevronLeft, ChevronRight, ChevronDown, ArrowUp, ArrowDown, Download } from 'lucide-react';
import clsx from 'clsx';
import { hybridAccountService } from '../../services/hybrid-account-service';
import { categoryService } from '../../services/category-service';
import type { Account } from '../../types/account.types';
import type { Category } from '../../types/category.types';
import { CategoryFilterDropdown } from './CategoryFilterDropdown';
import { AccountFilterDropdown } from './AccountFilterDropdown';
import { StatusFilterDropdown } from './StatusFilterDropdown';
import { TypeFilterDropdown } from './TypeFilterDropdown';
import { TransactionDatePicker, type DateRange } from './TransactionDatePicker';
// Date formatting imports removed - not currently used
import { useRegion } from '../../contexts/RegionContext';

// Maximum transactions to load when "All" is selected (reserved for future use)
 
export const MAX_ALL_LIMIT = 5000;

interface TransactionListProps {
  transactions: Transaction[];
  loading: boolean;
  onEdit: (transaction: Transaction) => void;
  onCategorise: (transaction: Transaction) => void;
  onSplit: (transaction: Transaction) => void;
  onTag: (transaction: Transaction) => void;
  onDelete: (transaction: Transaction) => void;
  onBulkAction: (action: string, transactionIds: string[]) => void;
  // Pagination props
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  // Filter props
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  filterType?: string;
  onFilterTypeChange?: (type: string) => void;
  filterStatus?: string;
  onFilterStatusChange?: (status: string) => void;
  filterCategory?: string[];
  onFilterCategoryChange?: (categoryIds: string[]) => void;
  filterAccount?: string | undefined;
  filterAccountIds?: string[] | undefined;
  onFilterAccountChange?: (accountId: string | undefined) => void;
  onFilterAccountIdsChange?: (accountIds: string[]) => void;
  dateFrom?: string;
  onDateFromChange?: (date: string) => void;
  dateTo?: string;
  onDateToChange?: (date: string) => void;
  onDateRangeChange?: (range: { from: string; to: string }) => void;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  // Sort props
  sortOrder?: 'asc' | 'desc';
  sortField?: 'date' | 'category' | 'amount';
  onSortChange?: (field: 'date' | 'category' | 'amount', order: 'asc' | 'desc') => void;
}

export function TransactionList({
  transactions,
  loading,
  onEdit,
  onCategorise,
  onSplit,
  onTag,
  onDelete,
  onBulkAction,
  // Pagination props
  total = 0,
  page = 1,
  limit = 50,
  totalPages = 1,
  onPageChange,
  onLimitChange,
  // Filter props
  searchQuery: externalSearchQuery = '',
  onSearchChange,
  filterType: externalFilterType = 'all',
  onFilterTypeChange,
  filterStatus: externalFilterStatus = 'all',
  onFilterStatusChange,
  filterCategory: externalFilterCategory = [],
  onFilterCategoryChange,
  filterAccount: externalFilterAccount = undefined,
  filterAccountIds: externalFilterAccountIds = undefined,
  onFilterAccountChange,
  onFilterAccountIdsChange,
  dateFrom: externalDateFrom = '',
  onDateFromChange,
  dateTo: externalDateTo = '',
  onDateToChange,
  onDateRangeChange,
  hasActiveFilters = false,
  onClearFilters,
  // Sort props
  sortOrder = 'desc',
  sortField = 'date',
  onSortChange
}: TransactionListProps): React.ReactElement {
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  const [accountsMap, setAccountsMap] = useState<Map<string, Account>>(new Map());
  const [categoriesMap, setCategoriesMap] = useState<Map<string, Category>>(new Map());
  const [showBulkCategorisePanel, setShowBulkCategorisePanel] = useState(false);
  const [showBulkNotesPanel, setShowBulkNotesPanel] = useState(false);
  const [showBulkMerchantPanel, setShowBulkMerchantPanel] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState(externalSearchQuery);
  
  // Get region context for currency formatting
  const { formatCurrency } = useRegion();
  
  // Sync local search input with external search query
  useEffect(() => {
    setSearchInput(externalSearchQuery);
  }, [externalSearchQuery]);
  
  // Use external filters
   
  void externalSearchQuery;
  const filterType = externalFilterType;
  const filterStatus = externalFilterStatus;
  const filterCategory = externalFilterCategory;
  const filterAccount = externalFilterAccount;
  const filterAccountIds = externalFilterAccountIds || [];
  const dateFrom = externalDateFrom;
  const dateTo = externalDateTo;

  // Calculate In Flow and Out Flow totals from filtered transactions
  // This should match the logic in calculateCurrentBalance for consistency
  const { inFlowTotal, outFlowTotal, difference } = useMemo(() => {
    // Filter by status (only completed and pending, exclude needs_review)
    const relevantTransactions = transactions.filter(t => 
      t.status === 'completed' || t.status === 'pending'
    );
    
    let inFlow = 0;
    let outFlow = 0;
    
    relevantTransactions.forEach((transaction) => {
      const amount = Math.abs(transaction.amount) || 0;
      
      if (transaction.type === 'credit') {
        inFlow += amount;
      } else if (transaction.type === 'debit') {
        outFlow += amount;
      } else if (transaction.type === 'transfer') {
        // For transfers, determine if this account is the source or destination
        const targetAccountId = filterAccount || transaction.accountId;
        
        if (transaction.sourceAccountId && transaction.destinationAccountId) {
          // If this account is the source, it's an outflow (money leaving)
          if (transaction.sourceAccountId === targetAccountId) {
            outFlow += amount;
          }
          // If this account is the destination, it's an inflow (money coming in)
          else if (transaction.destinationAccountId === targetAccountId) {
            inFlow += amount;
          }
        }
      }
    });
    
    const diff = inFlow - outFlow;
    
    return {
      inFlowTotal: inFlow,
      outFlowTotal: outFlow,
      difference: diff
    };
  }, [transactions, filterAccount]);

  // Fetch accounts to create a lookup map
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const result = await hybridAccountService.getAccounts({
          status: 'active'
        });
        if (result.data) {
          const map = new Map<string, Account>();
          result.data.accounts.forEach(account => {
            map.set(account.id, account);
          });
          setAccountsMap(map);
        }
      } catch (error) {
        console.error('Failed to load accounts for transaction list:', error);
      }
    };
    loadAccounts();
  }, []);

  // Fetch categories to create a lookup map
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const result = await categoryService.listCategories({ includeSystem: true });
        if (result.data) {
          const map = new Map<string, Category>();
          result.data.categories.forEach(category => {
            map.set(category.id, category);
          });
          setCategoriesMap(map);
        }
      } catch (error) {
        console.error('Failed to load categories for transaction list:', error);
      }
    };
    loadCategories();
  }, []);

  // Clean up selected transactions that no longer exist (e.g., after deletion)
  useEffect(() => {
    const transactionIds = new Set(transactions.map(t => t.id));
    
    setSelectedTransactions(prev => {
      const staleIds = Array.from(prev).filter(id => !transactionIds.has(id));
      if (staleIds.length > 0) {
        // Filter out any selected transaction IDs that don't exist in the current transactions list
        return new Set(Array.from(prev).filter(id => transactionIds.has(id)));
      }
      return prev;
    });
  }, [transactions]);

  // No client-side filtering - all filtering is done server-side
  // But we do client-side sorting for category and amount (date is sorted server-side)
  const filteredTransactions = useMemo(() => {
    if (!onSortChange || sortField === 'date') {
      // Date sorting is done server-side, so use transactions as-is
      return transactions;
    }

    // Client-side sorting for category and amount
    const sorted = [...transactions];
    sorted.sort((a, b) => {
      if (sortField === 'category') {
        const categoryA = a.categoryId ? categoriesMap.get(a.categoryId) : null;
        const categoryB = b.categoryId ? categoriesMap.get(b.categoryId) : null;
        const nameA = categoryA?.name || 'Uncategorised';
        const nameB = categoryB?.name || 'Uncategorised';
        const comparison = nameA.localeCompare(nameB);
        return sortOrder === 'asc' ? comparison : -comparison;
      } else if (sortField === 'amount') {
        const comparison = Math.abs(a.amount) - Math.abs(b.amount);
        return sortOrder === 'asc' ? comparison : -comparison;
      }
      return 0;
    });
    return sorted;
  }, [transactions, sortField, sortOrder, categoriesMap, onSortChange]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Only select filtered transactions, not all transactions
      setSelectedTransactions(new Set(filteredTransactions.map(t => t.id)));
    } else {
      // Clear all selections
      setSelectedTransactions(new Set());
    }
  };

  const handleSelectTransaction = (transactionId: string, selected: boolean) => {
    const newSelected = new Set(selectedTransactions);
    if (selected) {
      newSelected.add(transactionId);
    } else {
      newSelected.delete(transactionId);
    }
    setSelectedTransactions(newSelected);
  };

  /**
   * Export selected transactions to CSV
   */
  const handleExportCSV = () => {
    const selectedIds = Array.from(selectedTransactions);
    const selectedTransactionsData = transactions.filter(t => selectedIds.includes(t.id));
    
    if (selectedTransactionsData.length === 0) {
      return;
    }

    // CSV header
    const headers = [
      'Date',
      'Description',
      'Merchant Name',
      'Amount',
      'Type',
      'Category',
      'Status',
      'Account',
      'Notes',
      'Reference Number',
      'Payee',
      'Balance After',
      'Cheque Number',
      'Tags',
      'Created At',
      'Updated At'
    ];

    // Escape CSV field to handle commas, quotes, and newlines
    const escapeCSVField = (field: string | number | undefined | null): string => {
      if (field === null || field === undefined) {
        return '';
      }
      const str = String(field);
      if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    // Convert transaction to CSV row
    const transactionToCSVRow = (transaction: Transaction): string => {
      const account = accountsMap.get(transaction.accountId);
      const category = transaction.categoryId ? categoriesMap.get(transaction.categoryId) : undefined;
      const tags = transaction.tags?.map(t => t.name).join('; ') || '';
      
      const fields = [
        transaction.date,
        transaction.description,
        transaction.merchantName || '',
        transaction.amount.toString(),
        transaction.type,
        category?.name || '',
        transaction.status,
        account?.name || '',
        transaction.notes || '',
        transaction.referenceNumber || '',
        transaction.payee || '',
        transaction.balanceAfter?.toString() || '',
        transaction.chequeNumber || '',
        tags,
        transaction.createdAt,
        transaction.updatedAt
      ];
      
      return fields.map(escapeCSVField).join(',');
    };

    // Build CSV content
    const csvRows = [
      headers.map(escapeCSVField).join(','),
      ...selectedTransactionsData.map(transactionToCSVRow)
    ];

    const csvContent = csvRows.join('\n');
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `transactions_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Check selection state based on filtered transactions, not all transactions
  const allSelected = filteredTransactions.length > 0 && 
    filteredTransactions.every(t => selectedTransactions.has(t.id));
  const someSelected = filteredTransactions.some(t => selectedTransactions.has(t.id)) && 
    !allSelected;

  if (loading && transactions.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12">
        <div className="flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600"></div>
            <p className="text-lg text-gray-600 dark:text-gray-400 font-medium">Loading transactions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="space-y-4">
          {/* Search */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSearchChange?.(searchInput);
            }}
            className="relative"
          >
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search transactions by description, merchant name, or notes"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onSearchChange?.(searchInput);
                }
              }}
              className={`w-full pl-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500 ${
                searchInput ? 'pr-10' : 'pr-4'
              }`}
              aria-label="Search transactions"
              aria-describedby="search-description"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput('');
                  onSearchChange?.('');
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded"
                aria-label="Clear search"
              >
                <X className="h-5 w-5" />
              </button>
            )}
            <span id="search-description" className="sr-only">
              Search transactions by description, merchant name, or notes. Press Enter to search.
            </span>
          </form>

          {/* Filters */}
          <div className="flex flex-col gap-4">
            {/* Mobile Filter Toggle Button - Only visible on mobile */}
            <div className="md:hidden">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={clsx(
                  'flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors duration-200',
                  showFilters
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 border border-primary-200 dark:border-primary-800'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
                )}
                aria-expanded={showFilters}
                aria-controls="transaction-filters"
              >
                <Filter className="h-4 w-4" />
                <span>Filters</span>
                {hasActiveFilters && (
                  <span className="ml-1 px-2 py-0.5 text-xs bg-primary-600 text-white rounded-full font-semibold">
                    {[
                      dateFrom || dateTo ? 1 : 0,
                      filterAccount ? 1 : 0,
                      filterStatus !== 'all' ? 1 : 0,
                      filterType !== 'all' ? 1 : 0,
                      filterCategory && filterCategory.length > 0 ? 1 : 0,
                    ].reduce((a, b) => a + b, 0)}
                  </span>
                )}
              </button>
            </div>

            {/* Filter Controls - Hidden on mobile, visible on desktop */}
            <div 
              id="transaction-filters"
              className="hidden md:flex flex-wrap items-center gap-4"
            >
              {/* Date Range Selector */}
              <TransactionDatePicker
                value={{
                  from: dateFrom || '',
                  to: dateTo || '',
                }}
                onChange={(range: DateRange) => {
                  // Use combined handler if available to update both dates atomically
                  if (onDateRangeChange) {
                    onDateRangeChange(range);
                  } else {
                    // Fallback to separate handlers for backward compatibility
                    onDateFromChange?.(range.from);
                    onDateToChange?.(range.to);
                  }
                }}
              />

              {/* Account Filter */}
              <AccountFilterDropdown
                value={filterAccountIds.length === 0 ? filterAccount : undefined}
                valueIds={filterAccountIds.length > 0 ? filterAccountIds : undefined}
                onChange={(accountId) => {
                  onFilterAccountChange?.(accountId);
                  if (onFilterAccountIdsChange) {
                    onFilterAccountIdsChange([]);
                  }
                }}
                onChangeIds={(accountIds) => {
                  if (onFilterAccountIdsChange) {
                    onFilterAccountIdsChange(accountIds);
                  }
                  if (onFilterAccountChange && accountIds.length === 0) {
                    onFilterAccountChange(undefined);
                  }
                }}
              />

              <StatusFilterDropdown
                value={filterStatus}
                onChange={(value) => onFilterStatusChange?.(value)}
              />

              <TypeFilterDropdown
                value={filterType}
                onChange={(value) => onFilterTypeChange?.(value)}
              />

              {/* Category Filter */}
              <CategoryFilterDropdown
                value={filterCategory}
                onChange={(categoryIds) => onFilterCategoryChange?.(categoryIds)}
              />


              {hasActiveFilters && onClearFilters && (
                <button
                  onClick={onClearFilters}
                  className="flex items-center justify-center space-x-1 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
                  aria-label="Clear all filters"
                >
                  <X className="h-4 w-4" />
                  <span>Clear Filters</span>
                </button>
              )}
            </div>

            {/* Mobile Filter Panel - Only shown when expanded on mobile */}
            {showFilters && (
              <div className="md:hidden pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-col gap-4">
                  {/* Date Range Selector */}
                  <TransactionDatePicker
                    value={{
                      from: dateFrom || '',
                      to: dateTo || '',
                    }}
                    onChange={(range: DateRange) => {
                      // Use combined handler if available to update both dates atomically
                      if (onDateRangeChange) {
                        onDateRangeChange(range);
                      } else {
                        // Fallback to separate handlers for backward compatibility
                        onDateFromChange?.(range.from);
                        onDateToChange?.(range.to);
                      }
                    }}
                  />

                  {/* Account Filter */}
                  <AccountFilterDropdown
                    value={filterAccountIds.length === 0 ? filterAccount : undefined}
                    valueIds={filterAccountIds.length > 0 ? filterAccountIds : undefined}
                    onChange={(accountId) => {
                      onFilterAccountChange?.(accountId);
                      if (onFilterAccountIdsChange) {
                        onFilterAccountIdsChange([]);
                      }
                    }}
                    onChangeIds={(accountIds) => {
                      if (onFilterAccountIdsChange) {
                        onFilterAccountIdsChange(accountIds);
                      }
                      if (onFilterAccountChange && accountIds.length === 0) {
                        onFilterAccountChange(undefined);
                      }
                    }}
                  />

                  <StatusFilterDropdown
                    value={filterStatus}
                    onChange={(value) => onFilterStatusChange?.(value)}
                  />

                  <TypeFilterDropdown
                    value={filterType}
                    onChange={(value) => onFilterTypeChange?.(value)}
                  />

                  {/* Category Filter */}
                  <CategoryFilterDropdown
                    value={filterCategory}
                    onChange={(categoryIds) => onFilterCategoryChange?.(categoryIds)}
                  />


                  {/* Sort Dropdown for Mobile */}
                  {onSortChange && (
                    <div className="relative">
                      <select
                        value={`${sortField}-${sortOrder}`}
                        onChange={(e) => {
                          const [field, order] = e.target.value.split('-') as ['date' | 'category' | 'amount', 'asc' | 'desc'];
                          onSortChange(field, order);
                          if (onPageChange) {
                            onPageChange(1);
                          }
                        }}
                        className="px-4 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-primary-500 focus:border-primary-500 appearance-none cursor-pointer w-full"
                        aria-label="Sort transactions"
                      >
                        <option value="date-desc">Date: Newest first</option>
                        <option value="date-asc">Date: Oldest first</option>
                        <option value="category-asc">Category: A-Z</option>
                        <option value="category-desc">Category: Z-A</option>
                        <option value="amount-asc">Amount: Low to High</option>
                        <option value="amount-desc">Amount: High to Low</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                    </div>
                  )}

                  {hasActiveFilters && onClearFilters && (
                    <button
                      onClick={onClearFilters}
                      className="flex items-center justify-center space-x-1 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
                      aria-label="Clear all filters"
                    >
                      <X className="h-4 w-4" />
                      <span>Clear Filters</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Transaction Summary Stats - At the top of the table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {onLimitChange && total > 0 ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Showing <span className="font-semibold">{Math.min(total, (page - 1) * limit + transactions.length)}</span> of <span className="font-semibold">{total}</span> transaction{total !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Show:</span>
                <div className="flex items-center gap-1.5">
                  {[50, 100, 500, 1000].map((limitValue) => {
                    const isActive = limit === limitValue;
                    return (
                      <button
                        key={limitValue}
                        onClick={() => {
                          onLimitChange(limitValue);
                          if (onPageChange) {
                            onPageChange(1); // Reset to first page when changing limit
                          }
                        }}
                        disabled={loading}
                        className={clsx(
                          'px-3 py-1.5 text-sm font-medium rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed min-w-[44px]',
                          isActive
                            ? 'bg-primary-600 text-white shadow-sm'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                        )}
                        aria-label={`Show ${limitValue} per page`}
                        aria-pressed={isActive}
                      >
                        {limitValue}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
              {total === 0 ? (
                <p>
                  {hasActiveFilters
                    ? 'No transactions match your filters.'
                    : 'No transactions available yet.'}
                </p>
              ) : null}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
            {total === 0 ? null : (
              <>
                {transactions.length > 0 && (
                  <>
                    <span className="text-green-600 dark:text-green-400 font-medium">
                      In Flow: {formatCurrency(inFlowTotal)}
                    </span>
                    <span className="text-gray-400 dark:text-gray-500">•</span>
                    <span className="text-red-600 dark:text-red-400 font-medium">
                      Out Flow: {formatCurrency(outFlowTotal)}
                    </span>
                    <span className="text-gray-400 dark:text-gray-500">•</span>
                    <span className="font-medium" style={{ color: '#9333ea' }}>
                      Difference: {formatCurrency(difference)}
                    </span>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Actions Toolbar with Inline Categorise Panel */}
      {selectedTransactions.size > 0 && (
        <div className="space-y-4">
          <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-primary-900 dark:text-primary-200">
                {selectedTransactions.size} transaction{selectedTransactions.size !== 1 ? 's' : ''} selected
              </p>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => {
                    setShowBulkCategorisePanel(!showBulkCategorisePanel);
                    setShowBulkNotesPanel(false);
                    setShowBulkMerchantPanel(false);
                  }}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  aria-label={`Bulk categorise ${selectedTransactions.size} transactions`}
                  aria-expanded={showBulkCategorisePanel}
                >
                  Bulk Categorise
                </button>
                <button
                  onClick={() => onBulkAction('tag', Array.from(selectedTransactions))}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                  aria-label={`Bulk tag ${selectedTransactions.size} transactions`}
                >
                  Bulk Tag
                </button>
                <button
                  onClick={() => {
                    setShowBulkNotesPanel(!showBulkNotesPanel);
                    setShowBulkMerchantPanel(false);
                    setShowBulkCategorisePanel(false);
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  aria-label={`Bulk update notes for ${selectedTransactions.size} transactions`}
                  aria-expanded={showBulkNotesPanel}
                >
                  Bulk Notes
                </button>
                <button
                  onClick={() => {
                    setShowBulkMerchantPanel(!showBulkMerchantPanel);
                    setShowBulkNotesPanel(false);
                    setShowBulkCategorisePanel(false);
                  }}
                  className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                  aria-label={`Bulk update merchant name for ${selectedTransactions.size} transactions`}
                  aria-expanded={showBulkMerchantPanel}
                >
                  Bulk Merchant
                </button>
                <button
                  onClick={() => onBulkAction('delete', Array.from(selectedTransactions))}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                  aria-label={`Bulk delete ${selectedTransactions.size} transactions`}
                >
                  Bulk Delete
                </button>
                <button
                  onClick={handleExportCSV}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-100 hover:bg-green-200 dark:bg-green-900/30 dark:hover:bg-green-900/50 text-green-700 dark:text-green-300 rounded-lg font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 border border-green-600 dark:border-green-700"
                  aria-label={`Export ${selectedTransactions.size} transactions to CSV`}
                >
                  <Download className="h-4 w-4" />
                  <span>Export CSV</span>
                </button>
                <button
                  onClick={() => {
                    setSelectedTransactions(new Set());
                    setShowBulkCategorisePanel(false);
                    setShowBulkNotesPanel(false);
                    setShowBulkMerchantPanel(false);
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 rounded"
                  aria-label="Clear selection"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          </div>

          {/* Bulk Categorise Panel */}
          {showBulkCategorisePanel && (
            <BulkCategorisePanel
              isExpanded={showBulkCategorisePanel}
              onToggle={() => setShowBulkCategorisePanel(!showBulkCategorisePanel)}
              transactionIds={Array.from(selectedTransactions)}
              transactions={transactions}
              onComplete={(shouldClose = true) => {
                // Only close panel and clear selection if shouldClose is true
                // This allows the panel to stay open when there are failures
                if (shouldClose) {
                  setSelectedTransactions(new Set());
                  setShowBulkCategorisePanel(false);
                }
                // Note: Transactions will be refreshed automatically via TanStack Query invalidation
              }}
              onClose={() => setShowBulkCategorisePanel(false)}
            />
          )}

          {/* Bulk Notes Panel */}
          {showBulkNotesPanel && (
            <BulkNotesPanel
              isExpanded={showBulkNotesPanel}
              onToggle={() => setShowBulkNotesPanel(!showBulkNotesPanel)}
              transactionIds={Array.from(selectedTransactions)}
              transactions={transactions}
              onComplete={(shouldClose = true) => {
                if (shouldClose) {
                  setSelectedTransactions(new Set());
                  setShowBulkNotesPanel(false);
                }
              }}
              onClose={() => setShowBulkNotesPanel(false)}
            />
          )}

          {/* Bulk Merchant Panel */}
          {showBulkMerchantPanel && (
            <BulkMerchantPanel
              isExpanded={showBulkMerchantPanel}
              onToggle={() => setShowBulkMerchantPanel(!showBulkMerchantPanel)}
              transactionIds={Array.from(selectedTransactions)}
              transactions={transactions}
              onComplete={(shouldClose = true) => {
                if (shouldClose) {
                  setSelectedTransactions(new Set());
                  setShowBulkMerchantPanel(false);
                }
              }}
              onClose={() => setShowBulkMerchantPanel(false)}
            />
          )}
        </div>
      )}

      {/* Transactions - Mobile/Tablet Card View */}
      <div className="lg:hidden bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {filteredTransactions.length > 0 ? (
          <>
            {/* Select All Checkbox and Sort for Mobile */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 flex items-center justify-between">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = someSelected;
                  }}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  aria-label="Select all transactions"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Select all
                </span>
              </div>
              {onSortChange && (
                <div className="relative">
                  <select
                    value={`${sortField}-${sortOrder}`}
                    onChange={(e) => {
                      const [field, order] = e.target.value.split('-') as ['date' | 'category' | 'amount', 'asc' | 'desc'];
                      onSortChange(field, order);
                      if (onPageChange) {
                        onPageChange(1);
                      }
                    }}
                    className="px-3 py-1.5 pr-8 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-primary-500 focus:border-primary-500 appearance-none cursor-pointer"
                    aria-label="Sort transactions"
                  >
                    <option value="date-desc">Date: Newest first</option>
                    <option value="date-asc">Date: Oldest first</option>
                    <option value="category-asc">Category: A-Z</option>
                    <option value="category-desc">Category: Z-A</option>
                    <option value="amount-asc">Amount: Low to High</option>
                    <option value="amount-desc">Amount: High to Low</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" aria-hidden="true" />
                </div>
              )}
            </div>
            {filteredTransactions.map((transaction) => (
              <TransactionItem
                key={transaction.id}
                transaction={transaction}
                account={accountsMap.get(transaction.accountId)}
                category={transaction.categoryId ? categoriesMap.get(transaction.categoryId) : undefined}
                isSelected={selectedTransactions.has(transaction.id)}
                onSelect={(selected) => handleSelectTransaction(transaction.id, selected)}
                onEdit={() => onEdit(transaction)}
                onCategorise={() => onCategorise(transaction)}
                onSplit={() => onSplit(transaction)}
                onTag={() => onTag(transaction)}
                onDelete={() => onDelete(transaction)}
                viewMode="card"
              />
            ))}
          </>
        ) : (
          <div className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
            {hasActiveFilters
              ? 'No transactions match your filters. Try adjusting your search or clearing filters.'
              : 'No transactions available. Import transactions to get started.'}
          </div>
        )}
      </div>

      {/* Transactions Table - Desktop View */}
      <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" role="table" aria-label="Transactions list">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr role="row">
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = someSelected;
                    }}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    aria-label="Select all transactions"
                  />
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  onClick={() => {
                    if (onSortChange) {
                      const newOrder = sortField === 'date' && sortOrder === 'desc' ? 'asc' : 'desc';
                      onSortChange('date', newOrder);
                      if (onPageChange) {
                        onPageChange(1);
                      }
                    }
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span>Date & Merchant</span>
                    {sortField === 'date' && (
                      sortOrder === 'asc' ? (
                        <ArrowUp className="h-3 w-3" aria-hidden="true" />
                      ) : (
                        <ArrowDown className="h-3 w-3" aria-hidden="true" />
                      )
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  onClick={() => {
                    if (onSortChange) {
                      const newOrder = sortField === 'category' && sortOrder === 'asc' ? 'desc' : 'asc';
                      onSortChange('category', newOrder);
                      if (onPageChange) {
                        onPageChange(1);
                      }
                    }
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span>Category</span>
                    {sortField === 'category' && (
                      sortOrder === 'asc' ? (
                        <ArrowUp className="h-3 w-3" aria-hidden="true" />
                      ) : (
                        <ArrowDown className="h-3 w-3" aria-hidden="true" />
                      )
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  onClick={() => {
                    if (onSortChange) {
                      const newOrder = sortField === 'amount' && sortOrder === 'asc' ? 'desc' : 'asc';
                      onSortChange('amount', newOrder);
                      if (onPageChange) {
                        onPageChange(1);
                      }
                    }
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span>Amount</span>
                    {sortField === 'amount' && (
                      sortOrder === 'asc' ? (
                        <ArrowUp className="h-3 w-3" aria-hidden="true" />
                      ) : (
                        <ArrowDown className="h-3 w-3" aria-hidden="true" />
                      )
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredTransactions.length > 0 ? (
                filteredTransactions.map((transaction) => (
                  <TransactionItem
                    key={transaction.id}
                    transaction={transaction}
                    account={accountsMap.get(transaction.accountId)}
                    category={transaction.categoryId ? categoriesMap.get(transaction.categoryId) : undefined}
                    isSelected={selectedTransactions.has(transaction.id)}
                    onSelect={(selected) => handleSelectTransaction(transaction.id, selected)}
                    onEdit={() => onEdit(transaction)}
                    onCategorise={() => onCategorise(transaction)}
                    onSplit={() => onSplit(transaction)}
                    onTag={() => onTag(transaction)}
                    onDelete={() => onDelete(transaction)}
                    viewMode="table"
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                    {hasActiveFilters
                      ? 'No transactions match your filters. Try adjusting your search or clearing filters.'
                      : 'No transactions available. Import transactions to get started.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {(totalPages > 1 || onLimitChange) && (
        <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
              <span>
                Showing <span className="font-medium">{((page - 1) * limit) + 1}</span> to{' '}
                <span className="font-medium">{Math.min(page * limit, total)}</span> of{' '}
                <span className="font-medium">{total}</span> transactions
              </span>
            </div>
            {onLimitChange && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700 dark:text-gray-300">Show:</span>
                <div className="flex items-center space-x-1">
                  {[50, 100, 500, 1000].map((limitValue) => {
                    const isActive = limit === limitValue;
                    return (
                      <button
                        key={limitValue}
                        onClick={() => {
                          onLimitChange(limitValue);
                          if (onPageChange) {
                            onPageChange(1); // Reset to first page when changing limit
                          }
                        }}
                        disabled={loading}
                        className={clsx(
                          'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                          isActive
                            ? 'bg-primary-600 text-white'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                        )}
                        aria-label={`Show ${limitValue} per page`}
                        aria-pressed={isActive}
                      >
                        {limitValue}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          {totalPages > 1 && onPageChange && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onPageChange(page - 1)}
                disabled={page === 1 || loading}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
                <span>Previous</span>
              </button>
              
              <div className="flex items-center space-x-1">
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => onPageChange(pageNum)}
                      disabled={loading}
                      className={`px-3 py-2 rounded-lg text-sm font-medium ${
                        pageNum === page
                          ? 'bg-primary-600 text-white'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                      aria-label={`Go to page ${pageNum}`}
                      aria-current={pageNum === page ? 'page' : undefined}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => onPageChange(page + 1)}
                disabled={page === totalPages || loading}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                aria-label="Next page"
              >
                <span>Next</span>
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

